// PvP e Herói (Arena de Duelos): fila de batalha (lutas automáticas contra a mesma vocation), pontos do ciclo, ranking e Heróis atuais.
import { useEffect, useRef, useState } from 'preact/hooks';
import { HERO_BONUS_PCT, PVP_DAILY, PVP_MIN_FIGHTS, PVP_MIN_LEVEL, type PvpRow } from '../../../../shared/src/game/pvp';

export interface PvpState { inQueue: boolean; today: number; points: number; fights: number; wins: number; cycleEnds: number; hero: boolean }
const VOCS: Array<[string, string, string]> = [['knight', 'Knight', '🛡️'], ['paladin', 'Paladin', '🏹'], ['sorcerer', 'Sorcerer', '🔥'], ['druid', 'Druid', '🌿'], ['monk', 'Monk', '👊']];
const vocName = (id: string) => VOCS.find((v) => v[0] === id)?.[1] ?? id;
const vocIcon = (id: string) => VOCS.find((v) => v[0] === id)?.[2] ?? '⚔️';
const pad = (v: number) => String(v).padStart(2, '0');
const countdown = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return { h, m, s: s % 60 };
};
const mmss = (ms: number) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${pad(s % 60)}`; };

export function Pvp(props: { st: PvpState | null; vocation: string; level: number; me?: string; send: (op: 'join' | 'leave') => void }) {
  const [voc, setVoc] = useState(props.vocation);
  const [help, setHelp] = useState(false);
  const [, tick] = useState(0);
  const [data, setData] = useState<{ rows: PvpRow[]; heroes: Array<{ vocation: string; name: string }>; ends?: number } | null>(null);
  const queuedAt = useRef<number | null>(null);
  useEffect(() => { fetch(`/api/pvp?voc=${voc}`, { cache: 'no-cache' }).then((r) => r.json()).then(setData).catch(() => {}); }, [voc, props.st?.fights]);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const st = props.st;
  // tempo na fila: contado só no cliente, a partir de quando a janela viu o jogador entrar
  if (st?.inQueue && queuedAt.current == null) queuedAt.current = Date.now();
  if (!st?.inQueue) queuedAt.current = null;
  const now = Date.now();

  const ends = st?.cycleEnds ?? data?.ends ?? 0;
  const cd = countdown(ends - now);
  const losses = st ? st.fights - st.wins : 0;
  const rate = st && st.fights ? Math.round((st.wins / st.fights) * 100) : 0;
  const ownRows = voc === props.vocation;
  const myPos = ownRows && data && props.me ? data.rows.findIndex((r) => r.name === props.me) : -1;
  const lowLevel = props.level < PVP_MIN_LEVEL, doneToday = !!st && st.today >= PVP_DAILY;
  const eligible = !!st && st.fights >= PVP_MIN_FIGHTS;
  const rows = data?.rows ?? [];
  const podium = rows.slice(0, 3), rest = rows.slice(3);
  const myHero = data?.heroes.find((h) => h.vocation === props.vocation);

  const podiumSlot = (r: PvpRow | undefined, place: number) => (
    <div class={'pv-pod p' + place + (r && r.name === props.me ? ' me' : '')}>
      {r ? <>
        <span class="pv-pod-medal">{place === 1 ? '👑' : place === 2 ? '🥈' : '🥉'}</span>
        <b class="pv-pod-name" title={r.name}>{r.name}</b>
        <small>Lv {r.level} · {r.fights}L {r.wins}V</small>
        <span class="pv-pod-pts">{r.points} pts</span>
      </> : <small class="pv-pod-empty">vago</small>}
      <div class="pv-pod-base">{place}º</div>
    </div>
  );

  return (
    <div class="pv">
      {/* cabeçalho: duelo azul x vermelho */}
      <div class={'pv-head' + (st?.inQueue ? ' searching' : '')}>
        <div class="pv-side blue">
          <span class="pv-crest">{vocIcon(props.vocation)}</span>
          <div class="pv-side-txt"><b>{props.me ?? 'Você'}</b><small>{vocName(props.vocation)} · Lv {props.level}</small></div>
        </div>
        <div class="pv-vs"><span>VS</span></div>
        <div class="pv-side red">
          <div class="pv-side-txt"><b>{st?.inQueue ? 'Procurando...' : '???'}</b><small>{vocName(props.vocation)} rival</small></div>
          <span class="pv-crest foe">{st?.inQueue ? '🔍' : '❔'}</span>
        </div>
        <div class="pv-title">Arena de Duelos <button class="pv-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
      </div>

      {(help || (st && st.fights === 0 && !st.inQueue)) && (
        <div class="pv-steps">
          <div><i>⚔️</i><b>1. Entre na fila</b><small>Level {PVP_MIN_LEVEL}+. Até {PVP_DAILY} lutas por dia, só contra a sua vocation.</small></div>
          <div><i>🤖</i><b>2. Luta automática</b><small>Contra a cópia de outro jogador. Segue o seu Auto Helper e não tem perda.</small></div>
          <div><i>📈</i><b>3. Ganhe pontos</b><small>Quem vence toma pontos de quem perde. Ciclos de 48 horas.</small></div>
          <div><i>👑</i><b>4. Vire Herói</b><small>1º da vocation com {PVP_MIN_FIGHTS}+ lutas: +{HERO_BONUS_PCT}% de XP, dano, vida e mana no ciclo seguinte.</small></div>
        </div>
      )}

      {!st ? <p class="muted small">Carregando...</p> : <>
        {/* fila */}
        <div class={'pv-queue' + (st.inQueue ? ' on' : '')}>
          {st.inQueue ? <>
            <div class="pv-radar"><i /><i /><i /><span>⚔️</span></div>
            <div class="pv-queue-txt">
              <b>Procurando oponente<span class="pv-dots"><i>.</i><i>.</i><i>.</i></span></b>
              <small>Na fila há {mmss(now - (queuedAt.current ?? now))}. A faixa de pontos aumenta com a espera.</small>
            </div>
            <button class="tb-button" onClick={() => props.send('leave')}>Sair da fila</button>
          </> : <>
            <div class="pv-radar idle"><span>{lowLevel ? '🔒' : doneToday ? '✅' : '⚔️'}</span></div>
            <div class="pv-queue-txt">
              <b>{lowLevel ? `Liberado no level ${PVP_MIN_LEVEL}` : doneToday ? 'Lutas de hoje concluídas' : 'Pronto para lutar'}</b>
              <small>{lowLevel ? `Faltam ${PVP_MIN_LEVEL - props.level} levels.` : doneToday ? 'Volte amanhã para mais lutas.' : 'Entre na fila e continue jogando: a luta acontece sozinha.'}</small>
            </div>
            <button class="tb-button green" disabled={lowLevel || doneToday} onClick={() => props.send('join')}>
              {lowLevel ? `Level ${PVP_MIN_LEVEL}` : doneToday ? 'Feito hoje' : 'Entrar na fila'}</button>
          </>}
          <div class="pv-pips" title={`${st.today}/${PVP_DAILY} lutas hoje`}>
            {Array.from({ length: PVP_DAILY }, (_, i) => <i key={i} class={i < st.today ? 'used' : ''} />)}
            <span>Hoje {st.today}/{PVP_DAILY}</span>
          </div>
        </div>

        {/* placar do jogador */}
        <div class="pv-score">
          <div class="pv-stat main"><small>Pontos</small><b class="pv-gold">{st.points}</b></div>
          <div class="pv-stat"><small>Posição</small><b>{myPos >= 0 ? `#${myPos + 1}` : '—'}</b></div>
          <div class="pv-stat"><small>Vitórias</small><b class="pv-win">{st.wins}</b></div>
          <div class="pv-stat"><small>Derrotas</small><b class="pv-loss">{losses}</b></div>
          <div class="pv-stat"><small>Aproveitamento</small><b>{st.fights ? rate + '%' : '—'}</b></div>
          <div class="pv-ratio" title={`${st.wins} vitórias, ${losses} derrotas`}><i style={{ width: (st.fights ? rate : 50) + '%' }} /></div>
          <div class={'pv-elig' + (eligible ? ' ok' : '')}>
            <span>{eligible ? '✔ Elegível para Herói' : `Lutas para concorrer a Herói: ${st.fights}/${PVP_MIN_FIGHTS}`}</span>
            <div class="pv-elig-bar"><i style={{ width: Math.min(100, (st.fights / PVP_MIN_FIGHTS) * 100) + '%' }} /></div>
          </div>
        </div>
      </>}

      {/* Herói do ciclo + contagem regressiva */}
      <div class={'pv-hero' + (st?.hero ? ' me' : '')}>
        <div class="pv-hero-crown">👑</div>
        <div class="pv-hero-body">
          <div class="pv-hero-title">{st?.hero ? 'Você é o Herói deste ciclo!' : 'Heróis do ciclo'}</div>
          <small class="pv-hero-bonus">+{HERO_BONUS_PCT}% de XP, dano, vida e mana até o fim do ciclo</small>
          <div class="pv-hero-list">
            {VOCS.map(([id, label, icon]) => {
              const h = data?.heroes.find((x) => x.vocation === id);
              return <span key={id} class={'pv-hero-chip' + (id === props.vocation ? ' mine' : '') + (h ? '' : ' none') + (h && h.name === props.me ? ' self' : '')} title={label}>
                <i>{icon}</i>{h ? h.name : '—'}</span>;
            })}
          </div>
          {!st?.hero && myHero && <small class="muted">Herói da sua vocation: <b class="pv-gold">{myHero.name}</b></small>}
        </div>
        <div class="pv-clock" title="Quando o ciclo acaba, o 1º de cada vocation vira Herói">
          <small>Ciclo acaba em</small>
          <div class="pv-clock-digits">{ends ? <><b>{pad(cd.h)}</b>:<b>{pad(cd.m)}</b>:<b>{pad(cd.s)}</b></> : <b>--</b>}</div>
          <small class="pv-clock-u"><span>h</span><span>min</span><span>s</span></small>
        </div>
      </div>

      {/* ranking */}
      <div class="pv-sec">Ranking do ciclo</div>
      <div class="pv-tabs">
        {VOCS.map(([id, label, icon]) => <button key={id} class={'pv-tab' + (voc === id ? ' on' : '') + (id === props.vocation ? ' mine' : '')} onClick={() => setVoc(id)}><i>{icon}</i>{label}</button>)}
      </div>
      {data && !rows.length ? <p class="muted small pv-empty">Ninguém lutou neste ciclo ainda. Seja o primeiro!</p> : <>
        <div class="pv-podium">{podiumSlot(podium[1], 2)}{podiumSlot(podium[0], 1)}{podiumSlot(podium[2], 3)}</div>
        {!!rest.length && <div class="pv-table">
          <div class="pv-row head"><span>#</span><span>Nome</span><span>Lutas (V)</span><span>Level</span><span>Pontos</span></div>
          {rest.map((r, i) => <div key={r.name} class={'pv-row' + (r.name === props.me ? ' me' : '')}><span>{i + 4}</span><span>{r.name}</span><span>{r.fights} ({r.wins})</span><span>{r.level}</span><span>{r.points}</span></div>)}
        </div>}
        {ownRows && data && st && st.fights > 0 && myPos < 0 && <p class="muted small">Você está fora do top 100 desta vocation.</p>}
      </>}
    </div>
  );
}
